import type {
  AddOnRequest,
  AssignSubscriptionRequest,
  ChangeEmailRequest,
  ChangeEmailResponse,
  ResolveReferenceResponse,
  UserActivityResponse,
  CreateSiteUserRequest,
  UpdateUserPasswordRequest,
  UserBillingResponse,
  UserLookupResponse,
  UserOperationResponse,
  AdminSection,
  AdminCommentsListResponse,
  AdminCommentUserSearchResponse,
  CommentUserType,
  AnalystReportsResponse,
  FirmSuggestionsResponse,
  ApiResponse,
  CheckoutDiscountConfigResponse,
  UpdateCheckoutDiscountConfigRequest,
  Article,
  ArticlesListResponse,
  AssetTranslationAggregate,
  ChangePasswordRequest,
  CreateUserRequest,
  CreateUserResponse,
  ListUsersResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  ResetPasswordResponse,
  SetCommentUserBlockedResponse,
  SetCommentUserProfileResponse,
  SetCommentUserTypeResponse,
  TranslationsListResponse,
  UpdateArticle,
  GetPrivateCompanyResponse,
  IngestPrivateCompanyRequest,
  IngestPrivateCompanyResponse,
  AssetLogoExistsResponse,
  UploadAssetLogoRequest,
  UploadAssetLogoResponse,
  AdsFileName,
  GetAdsFileResponse,
  UpdateAdsFileRequest,
  UpdateAdsFileResponse,
  DataTriggerKey,
  FullStockRunResponse,
  GetPayloadResponse,
  GetPipelineCheckpointsResponse,
  PurgeRequest,
  PurgeResponse,
  PurgeStockPageResponse,
  ResolveStockResponse,
  RunStockEtlRequest,
  RunTriggerResponse,
  StockEtlProcessKey,
  StockPageTypeKey,
  UpdateStockTabRequest,
  UpdateStockTabResponse,
  ApiClient,
  ApiClientScalars,
  ApiClientSummary,
  B2bChange,
  CanaryRun,
  B2bConfig,
  B2bOperation,
  B2bSummary,
  B2bWriteMode,
  CatalogEndpoint,
  CatalogView,
  EndpointDoc,
  ApiUsageDay,
  ApiUsageLatest,
  WidgetEvidence,
  WidgetHistoryPoint,
  WidgetLatest,
  WidgetRunSummary,
  WritingQualityArticleDetailResponse,
  WritingQualityArticlesResponse,
} from "../../../shared/index";

// ── B2B section payloads (shapes the API returns, not stored documents) ──

/** Mirrors `PublishStatus` in `apps/admin/api/src/lib/b2b/publish.ts`. */
export interface B2bPublishStatus {
  generatedAt: string | null;
  version: number;
  counts: { guides: number; endpoints: number; redirects: number };
  changedSinceLastPublish: number;
  wouldPublish: { guides: number; endpoints: number };
  heldBack: { drafts: number; hidden: number };
  catalogUrl: string;
}

/** Mirrors `PublishResult`. */
export interface B2bPublishResult {
  catalogUrl: string;
  version: number;
  generatedAt: string;
  counts: { guides: number; endpoints: number; redirects: number };
  heldBack: { drafts: number; hidden: number };
  uploaded: boolean;
  purge: { ok: boolean; status: number; detail?: string } | null;
}

export interface B2bClientListResponse {
  items: ApiClientSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface B2bEffectiveEndpoint {
  featureUrlId: number;
  featureUrl: string;
  /** Which grant unlocks it — a grant path, or "full". */
  via: string;
}

export interface B2bClientDetail {
  client: ApiClient;
  effectiveAccess: B2bEffectiveEndpoint[];
}

export interface B2bWriteResponse {
  mode: B2bWriteMode;
  applied: boolean;
  change: B2bChange;
}

export interface B2bCreateResponse extends B2bWriteResponse {
  clientId: number;
  clientKey: string;
  /** Shown once, never returned again. Null on a dry run. */
  token: string | null;
}

export interface B2bLookups {
  features: { featureId: number; name: string }[];
  newsCategories: { newsCategoryId: number; slug: string }[];
  products: { productId: number; productName: string; description: string | null }[];
}

export interface B2bPendingRefresh {
  clientKey: string;
  changeId: string;
  targets: string[];
  queuedAt: string;
  attempts: number;
}

export interface B2bClientFilters {
  q?: string;
  active?: boolean;
  authType?: string;
  environment?: string;
  withoutGrants?: boolean;
  page?: number;
  pageSize?: number;
}

const TOKEN_KEY = "tr_admin_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Statuses that mean a proxy/gateway gave up (or the connection dropped)
 * rather than the API returning a real error. Status 0 = fetch itself rejected
 * (network/DNS/CORS). 524 is Cloudflare's origin-timeout code — the case that
 * makes slow requests surface as "Network error".
 */
export const GATEWAY_TIMEOUT_STATUSES = new Set([0, 408, 502, 503, 504, 522, 524]);

export function isLikelyTimeoutError(e: unknown): boolean {
  return e instanceof ApiError && GATEWAY_TIMEOUT_STATUSES.has(e.status);
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(`/api${path}`, { ...init, headers });
  } catch {
    // Fetch itself rejected (connection dropped / DNS / CORS / abort) — no
    // HTTP status is available, so use 0.
    throw new ApiError(0, "Unable to reach the server");
  }

  let payload: ApiResponse<T>;
  try {
    payload = await response.json();
  } catch {
    // Non-JSON body: usually a proxy/gateway error page (e.g. a Cloudflare 524
    // timeout). statusText is empty over HTTP/2, so build a clearer message.
    const message = GATEWAY_TIMEOUT_STATUSES.has(response.status)
      ? "The server took too long to respond (gateway timeout)"
      : response.statusText || `Request failed (HTTP ${response.status})`;
    throw new ApiError(response.status, message);
  }

  if (!response.ok || payload.error) {
    throw new ApiError(response.status, payload.error || response.statusText);
  }
  return payload.data as T;
}

/** Every site-user write shares one envelope, so they share one helper. */
function op(
  path: string,
  body: unknown,
  method: "POST" | "DELETE" = "POST"
): Promise<UserOperationResponse> {
  return request<UserOperationResponse>(path, {
    method,
    body: JSON.stringify(body),
  });
}

export const api = {
  login: (body: LoginRequest) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  me: () => request<MeResponse>("/auth/me"),
  logout: () => request<{ success: true }>("/auth/logout", { method: "POST" }),
  changePassword: (body: ChangePasswordRequest) =>
    request<LoginResponse>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminUsers: {
    list: () => request<ListUsersResponse>("/admin/users"),
    create: (body: CreateUserRequest) =>
      request<CreateUserResponse>("/admin/users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ success: true }>(`/admin/users/${id}`, { method: "DELETE" }),
    setPermission: (id: string, section: AdminSection, enabled: boolean) =>
      request<{ user: import("../../../shared/index").AdminUser }>(
        `/admin/users/${id}/permissions`,
        {
          method: "PATCH",
          body: JSON.stringify({ section, enabled }),
        }
      ),
    setStatus: (id: string, isActive: boolean) =>
      request<{ user: import("../../../shared/index").AdminUser }>(
        `/admin/users/${id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ isActive }),
        }
      ),
    resetPassword: (id: string) =>
      request<ResetPasswordResponse>(`/admin/users/${id}/reset-password`, {
        method: "POST",
      }),
  },

  ilWebsite: {
    listArticles: (params: { page: number; search?: string }) => {
      const qs = new URLSearchParams({ page: String(params.page) });
      if (params.search) qs.set("search", params.search);
      return request<ArticlesListResponse>(`/il-website/articles?${qs}`);
    },
    getArticle: (id: string) =>
      request<Article>(`/il-website/articles/${encodeURIComponent(id)}`),
    updateArticle: (id: string, body: UpdateArticle) =>
      request<Article>(`/il-website/articles/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    deleteArticle: (id: string) =>
      request<{ success: true; deleted: string }>(
        `/il-website/articles/${encodeURIComponent(id)}`,
        { method: "DELETE" }
      ),
  },

  writingQuality: {
    articles: (params: { from?: string; to?: string }) => {
      const qs = new URLSearchParams();
      if (params.from) qs.set("from", params.from);
      if (params.to) qs.set("to", params.to);
      const suffix = qs.toString();
      return request<WritingQualityArticlesResponse>(
        `/writing-quality/articles${suffix ? `?${suffix}` : ""}`
      );
    },
    article: (id: string) =>
      request<WritingQualityArticleDetailResponse>(
        `/writing-quality/articles/${encodeURIComponent(id)}`
      ),
  },

  analystReports: {
    list: (params: {
      ticker?: string;
      firm?: string;
      firmId?: number;
      limit?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params.ticker) qs.set("ticker", params.ticker);
      if (params.firmId !== undefined) qs.set("firmId", String(params.firmId));
      else if (params.firm) qs.set("firm", params.firm);
      if (params.limit) qs.set("limit", String(params.limit));
      const suffix = qs.toString();
      return request<AnalystReportsResponse>(
        `/analyst-reports${suffix ? `?${suffix}` : ""}`
      );
    },
    firms: (q?: string) => {
      const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
      return request<FirmSuggestionsResponse>(`/analyst-reports/firms${suffix}`);
    },
  },

  translations: {
    list: (params: { page: number; limit: number; search?: string }) => {
      const qs = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.search) qs.set("search", params.search);
      return request<TranslationsListResponse>(`/translations?${qs}`);
    },
    get: (ticker: string) =>
      request<AssetTranslationAggregate>(
        `/translations/${encodeURIComponent(ticker)}`
      ),
    create: (body: { stockId: number; companyName: string }) =>
      request<{ stockId: number; companyName: string }>(`/translations`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (ticker: string, body: AssetTranslationAggregate) =>
      request<{ success: true; stockId: number }>(
        `/translations/${encodeURIComponent(ticker)}`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        }
      ),
  },

  checkout: {
    getDiscountConfig: () =>
      request<CheckoutDiscountConfigResponse>("/checkout/discount-config"),
    updateDiscountConfig: (body: UpdateCheckoutDiscountConfigRequest) =>
      request<CheckoutDiscountConfigResponse>("/checkout/discount-config", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },

  comments: {
    list: (params: {
      page: number;
      limit: number;
      postSlug?: string;
      userId?: string;
      authorSlug?: string;
      deleted?: boolean;
      reported?: boolean;
      ai?: "only" | "exclude";
    }) => {
      const qs = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.postSlug) qs.set("postSlug", params.postSlug);
      if (params.userId) qs.set("userId", params.userId);
      if (params.authorSlug) qs.set("authorSlug", params.authorSlug);
      if (params.deleted) qs.set("deleted", "true");
      if (params.reported) qs.set("reported", "true");
      if (params.ai) qs.set("ai", params.ai);
      return request<AdminCommentsListResponse>(`/comments?${qs}`);
    },
    searchUsers: (q: string) =>
      request<AdminCommentUserSearchResponse>(
        `/comments/search-users?q=${encodeURIComponent(q)}`
      ),
    delete: (id: string) =>
      request<{ success: true }>(`/comments/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    restore: (id: string) =>
      request<{ success: true }>(
        `/comments/${encodeURIComponent(id)}/restore`,
        { method: "PATCH" }
      ),
    setUserType: (userId: string, userType: CommentUserType, staticUserId?: string) =>
      request<SetCommentUserTypeResponse>(
        `/comments/set-user-type/${encodeURIComponent(userId)}`,
        { method: "POST", body: JSON.stringify({ userType, staticUserId }) }
      ),
    setUserBlocked: (staticUserId: string, isBlocked: boolean) =>
      request<SetCommentUserBlockedResponse>(
        `/comments/set-blocked/${encodeURIComponent(staticUserId)}`,
        { method: "POST", body: JSON.stringify({ isBlocked }) }
      ),
    setUserProfile: (
      staticUserId: string,
      fields: { displayName?: string; bio?: string }
    ) =>
      request<SetCommentUserProfileResponse>(
        `/comments/set-profile/${encodeURIComponent(staticUserId)}`,
        { method: "POST", body: JSON.stringify(fields) }
      ),
  },

  privateCompanies: {
    get: (id: number) =>
      request<GetPrivateCompanyResponse>(
        `/private-companies/${encodeURIComponent(id)}`
      ),
    ingest: (body: IngestPrivateCompanyRequest) =>
      request<IngestPrivateCompanyResponse>("/private-companies/ingest", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  assetsLogo: {
    exists: (ticker: string) =>
      request<AssetLogoExistsResponse>(
        `/assets-logo/exists?ticker=${encodeURIComponent(ticker)}`
      ),
    upload: (body: UploadAssetLogoRequest) =>
      request<UploadAssetLogoResponse>("/assets-logo/upload", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  ads: {
    // The environment is fixed per deployment (resolved server-side from
    // APP_VER), so the client only picks the file.
    getFile: (file: AdsFileName) =>
      request<GetAdsFileResponse>(
        `/ads/file?file=${encodeURIComponent(file)}`
      ),
    updateFile: (body: UpdateAdsFileRequest) =>
      request<UpdateAdsFileResponse>("/ads/file", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },

  b2b: {
    summary: () => request<B2bSummary>("/b2b/summary"),
    settings: () =>
      request<{
        config: Omit<B2bConfig, "canaryTokenEncrypted"> & { canaryTokenSet: boolean };
        pendingRefresh: B2bPendingRefresh[];
      }>("/b2b/settings"),
    saveSettings: (body: {
      writeModes?: Record<B2bOperation, B2bWriteMode>;
      notifyTo?: string[];
      canaryKey?: string | null;
    }) =>
      request<B2bConfig>("/b2b/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    catalog: () => request<CatalogEndpoint[]>("/b2b/catalog"),
    catalogView: () => request<CatalogView>("/b2b/catalog/view"),
    endpointDoc: (slug: string) => request<EndpointDoc>(`/b2b/catalog/${slug}`),
    createEndpoint: (body: {
      featureUrlName: string;
      description?: string | null;
      productIds?: number[];
      /** Set to document an endpoint already in the catalog: its row is edited, not created. */
      featureUrlId?: number;
      doc?: Omit<EndpointDoc, "updatedAt" | "updatedBy" | "version" | "featureUrlId">;
    }) =>
      request<{ applied: boolean; mode: B2bWriteMode; doc: EndpointDoc | null }>("/b2b/catalog", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    saveEndpoint: (
      slug: string,
      body: {
        doc?: Partial<EndpointDoc>;
        expectedVersion?: number;
        catalog?: {
          featureUrlId: number;
          featureUrlName?: string;
          description?: string | null;
          productIds?: number[];
        };
      }
    ) =>
      request<{ doc: EndpointDoc; catalog: { applied: boolean; mode: B2bWriteMode } | null }>(
        `/b2b/catalog/${slug}`,
        { method: "PUT", body: JSON.stringify(body) }
      ),
    publishStatus: () => request<B2bPublishStatus>("/b2b/catalog/publish/status"),
    publish: () =>
      request<{ applied: boolean; mode: B2bWriteMode; result: B2bPublishResult }>(
        "/b2b/catalog/publish",
        { method: "POST" }
      ),
    usageConfig: () =>
      request<{ configured: boolean; maxRangeDays: number }>("/b2b/usage/config"),
    apiUsage: {
      latest: () => request<ApiUsageLatest | null>("/b2b/api-usage/latest"),
      daily: (key: string) => request<ApiUsageDay[]>(`/b2b/api-usage/daily?key=${encodeURIComponent(key)}`),
    },
    widgets: {
      latest: () => request<WidgetLatest | null>("/b2b/widgets/latest"),
      runs: (limit = 50) => request<WidgetRunSummary[]>(`/b2b/widgets/runs?limit=${limit}`),
      history: (key: string) =>
        request<WidgetHistoryPoint[]>(`/b2b/widgets/history?key=${encodeURIComponent(key)}`),
      evidence: (path: string) =>
        request<WidgetEvidence | null>(`/b2b/widgets/evidence?path=${encodeURIComponent(path)}`),
      /** The page URL with a fresh token / ib handshake, ready to open. */
      open: (key: string) => request<{ url: string }>(`/b2b/widgets/open?key=${encodeURIComponent(key)}`),
    },
    usage: (
      shape: "timeseries" | "by-endpoint" | "by-status" | "rows",
      filter: {
        clients?: string;
        endpoints?: string;
        days: number;
        status?: string;
        limit?: number;
      }
    ) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== "") params.set(key, String(value));
      }
      return request<{ rows: Record<string, unknown>[]; cache: "hit" | "miss" }>(
        `/b2b/usage/${shape}?${params.toString()}`
      );
    },
    usageLastSeen: (clientKeys: string[]) =>
      request<Record<string, string>>(
        `/b2b/usage/last-seen?clients=${encodeURIComponent(clientKeys.join(","))}`
      ),
    runCanary: (kind: "check" | "cycle") =>
      request<CanaryRun>(`/b2b/canary/run?kind=${kind}`, { method: "POST" }),
    saveCanaryCredentials: (body: { key: string | null; token: string | null }) =>
      request<{ saved: boolean }>("/b2b/canary/credentials", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    revertChange: (changeId: string) =>
      request<{ applied: boolean; mode: B2bWriteMode; change: B2bChange }>(
        `/b2b/changes/${changeId}/revert`,
        { method: "POST" }
      ),
    change: (changeId: string) => request<B2bChange>(`/b2b/changes/${changeId}`),
    previewToken: (slug: string) =>
      request<{ token: string; expiresAt: string; url: string }>(
        `/b2b/catalog/${slug}/preview-token`,
        { method: "POST" }
      ),
    lookups: () => request<B2bLookups>("/b2b/lookups"),
    listClients: (filters: B2bClientFilters = {}) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== "") params.set(key, String(value));
      }
      const query = params.toString();
      return request<B2bClientListResponse>(`/b2b/clients${query ? `?${query}` : ""}`);
    },
    getClient: (id: number) => request<B2bClientDetail>(`/b2b/clients/${id}`),
    createClient: (body: {
      name: string;
      authType?: ApiClient["authType"];
      featureUrlIds?: number[];
      copyFromClientId?: number;
      presetId?: string;
    }) =>
      request<B2bCreateResponse>("/b2b/clients", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateClient: (id: number, body: Partial<ApiClientScalars>) =>
      request<B2bWriteResponse>(`/b2b/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    setAccess: (
      id: number,
      body: {
        featureUrlIds: number[];
        flags?: { featureId: number; enabled: boolean; customValue: string | null }[];
        countryIds?: number[];
        newsCategoryIds?: number[];
      }
    ) =>
      request<B2bWriteResponse>(`/b2b/clients/${id}/access`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    rotateToken: (id: number) =>
      request<B2bWriteResponse & { token: string | null }>(`/b2b/clients/${id}/token/rotate`, {
        method: "POST",
      }),
    revealToken: (id: number) =>
      request<{ token: string }>(`/b2b/clients/${id}/token/reveal`, { method: "POST" }),
    deactivate: (id: number) =>
      request<B2bWriteResponse>(`/b2b/clients/${id}/deactivate`, { method: "POST" }),
    reactivate: (id: number) =>
      request<B2bWriteResponse>(`/b2b/clients/${id}/reactivate`, { method: "POST" }),
    changes: (filters: { clientId?: number; limit?: number; skip?: number; mode?: string } = {}) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined) params.set(key, String(value));
      }
      const query = params.toString();
      return request<{ items: B2bChange[]; total: number }>(
        `/b2b/changes${query ? `?${query}` : ""}`
      );
    },
    retryPropagation: (clientKey: string) =>
      request<{ results: unknown[] }>("/b2b/propagation/retry", {
        method: "POST",
        body: JSON.stringify({ clientKey }),
      }),
  },

  data: {
    getCheckpoints: () =>
      request<GetPipelineCheckpointsResponse>("/data/pipeline/checkpoints"),
    runTrigger: (key: DataTriggerKey) =>
      request<RunTriggerResponse>(`/data/triggers/${key}/run`, {
        method: "POST",
      }),
    getPayload: (ticker: string, pageKey: StockPageTypeKey) =>
      request<GetPayloadResponse>(
        `/data/payloads/${encodeURIComponent(ticker)}/${pageKey}`
      ),
    resolveStock: (ticker: string) =>
      request<ResolveStockResponse>(
        `/data/stocks/${encodeURIComponent(ticker)}`
      ),
    runStockEtl: (
      ticker: string,
      processKey: StockEtlProcessKey,
      body: RunStockEtlRequest = {}
    ) =>
      request<RunTriggerResponse>(
        `/data/stocks/${encodeURIComponent(ticker)}/etl/${processKey}/run`,
        { method: "POST", body: JSON.stringify(body) }
      ),
    purgeStockPage: (ticker: string, pageKey: StockPageTypeKey) =>
      request<PurgeStockPageResponse>(
        `/data/stocks/${encodeURIComponent(ticker)}/pages/${pageKey}/purge`,
        { method: "POST" }
      ),
    updateStockTab: (
      ticker: string,
      pageKey: StockPageTypeKey,
      body: UpdateStockTabRequest = {}
    ) =>
      request<UpdateStockTabResponse>(
        `/data/stocks/${encodeURIComponent(ticker)}/pages/${pageKey}/update`,
        { method: "POST", body: JSON.stringify(body) }
      ),
    fullStockRun: (ticker: string) =>
      request<FullStockRunResponse>(
        `/data/stocks/${encodeURIComponent(ticker)}/full-run`,
        { method: "POST" }
      ),
    purge: (body: PurgeRequest) =>
      request<PurgeResponse>("/data/purge", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // User Management — site users (customers), not admin-panel logins.
  siteUsers: {
    lookup: (email: string) =>
      request<UserLookupResponse>(
        `/site-users/lookup?email=${encodeURIComponent(email)}`
      ),
    resolve: (q: string) =>
      request<ResolveReferenceResponse>(
        `/site-users/resolve?q=${encodeURIComponent(q)}`
      ),
    activity: (emails: string[]) =>
      request<UserActivityResponse>(
        `/site-users/activity?emails=${encodeURIComponent(emails.join(","))}`
      ),
    billing: (email: string) =>
      request<UserBillingResponse>(
        `/site-users/billing?email=${encodeURIComponent(email)}`
      ),
    create: (body: CreateSiteUserRequest) =>
      op("/site-users", body),
    assignSubscription: (body: AssignSubscriptionRequest) =>
      op("/site-users/subscription", body),
    addAddOn: (body: AddOnRequest) => op("/site-users/addon", body),
    removeAddOn: (body: AddOnRequest) =>
      op("/site-users/addon", body, "DELETE"),
    changeEmail: (body: ChangeEmailRequest) =>
      request<ChangeEmailResponse>("/site-users/email/change", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    verifyEmail: (email: string) => op("/site-users/email/verify", { email }),
    stopEmails: (email: string) => op("/site-users/email/stop", { email }),
    updatePassword: (body: UpdateUserPasswordRequest) =>
      op("/site-users/password", body),
    activateTermsOfUse: (email: string) =>
      op("/site-users/terms-of-use", { email }),
    addPortfolio: (email: string) => op("/site-users/portfolio", { email }),
    deleteUser: (email: string) => op("/site-users", { email }, "DELETE"),
  },
};
